import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Car, Baby, UtensilsCrossed, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  spa: Sparkles,
  massage: Heart,
  biluthyrning: Car,
  barn: Baby,
  mat: UtensilsCrossed,
};

const categoryImages = {
  spa: 'https://customer-assets.emergentagent.com/job_direction-mapper-sv/artifacts/4wffcu67_spa%20tjej%20.png',
  massage: 'https://customer-assets.emergentagent.com/job_direction-mapper-sv/artifacts/jaqpl3uf_masage.png',
  biluthyrning: 'https://customer-assets.emergentagent.com/job_direction-mapper-sv/artifacts/tz5ta9zw_hyr%20bil%20.png',
  barn: 'https://customer-assets.emergentagent.com/job_direction-mapper-sv/artifacts/jjfdhjhd_spa%20barn.png',
  mat: 'https://customer-assets.emergentagent.com/job_direction-mapper-sv/artifacts/5hfipn7a_fr%C3%A4scht.png',
};

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="categories-page">
      {/* Header */}
      <section className="bg-white border-b border-[#2C3E36]/10">
        <div className="container-main py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-['Avenir_Next'] text-4xl md:text-5xl text-[#1A1A1A] mb-4">
              Utforska kategorier
            </h1>
            <p className="text-[#595959] text-lg max-w-2xl mx-auto">
              Hitta din nästa upplevelse inom våra noggrant utvalda kategorier
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories grid */}
      <section className="section-padding">
        <div className="container-main">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-80 skeleton rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {categories.map((cat, i) => {
                const Icon = categoryIcons[cat.id] || Sparkles;
                return (
                  <motion.div
                    key={cat.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Link
                      to={`/upplevelser?category=${cat.id}`}
                      className="group block relative h-80 rounded-2xl overflow-hidden"
                      data-testid={`category-card-${cat.id}`}
                    >
                      <img
                        src={categoryImages[cat.id]}
                        alt={cat.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                        <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4">
                          <Icon className="w-7 h-7" />
                        </div>
                        <h2 className="font-['Avenir_Next'] text-3xl mb-2">
                          {cat.name}
                        </h2>
                        <div className="flex items-center justify-between">
                          <span className="text-white/80">
                            {cat.count} upplevelser
                          </span>
                          <span className="flex items-center gap-2 text-[#C8A67B] group-hover:gap-4 transition-all">
                            Utforska
                            <ArrowRight className="w-5 h-5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
